import BoxState, { EVENT_TYPES } from '../../scripts/lib/box-state.js'
import BoxItemsSessionManager from '../../scripts/lib/box-items-session-manager.js'
import { getDynamicBundleItems } from '@rechargeapps/storefront-client'
import CustomerService from '../../scripts/lib/customer-service.js'
import { setTextContent, setInnerHTML, setDisabled } from '../../scripts/lib/dom.js'
import { getFeedingAmount, convertOunceToPound } from '../../scripts/lib/recommendation.js'
import { transformPetsForRecommendation } from '../../scripts/lib/pet-profile.js'
import { renderPetRecommendationHtml } from '../../scripts/lib/pet-display-helpers.js'
import { findItemBySpeciesProtein } from '../../scripts/lib/distribution.js'
import { shouldAddGwpToCartFromSelections, buildGwpCartItem } from '../../scripts/lib/gwp-helpers.js'
import { DEFAULT_MEAL_PLAN_URL, PRODUCT_PACKAGE_WEIGHT_LBS, MIN_SAMPLER_TRIAL_WEIGHT_LBS } from '../../scripts/lib/constants.js'
import { calculateSamplerSubscriptionQuantities } from '../../scripts/lib/samplers.js'
import { buildBundleSelections, buildCartItems, updateThawingContainerVisibility } from '../../scripts/lib/bundle-helpers.js'
import { getSellingPlanForItem } from '../../scripts/lib/selling-plan-helpers.js'

const MINIMUM_TRIAL_DURATION_WEEKS = 2

const GROUP_SORT_ORDER = {
  DOG: 0,
  PUPPY: 1,
  CAT: 2,
  KITTEN: 3
}

class BoxSummaryView extends HTMLElement {
  #boxState = null
  #selections = []
  #boxItems = {}
  #selectionMetaMap = new Map()
  #subscriptionData = null
  #trialDuration = null
  #subscribeButton = null
  #samplerCardsContainer = null
  #subscriptionCardsContainer = null
  #samplerWeightEl = null
  #samplerPriceEl = null
  #samplerOriginalPriceEl = null
  #subscriptionWeightEl = null
  #subscriptionPriceEl = null
  #subscriptionPerDayEl = null
  #overviewSamplerWeight = null
  #overviewSamplerOriginal = null
  #overviewSamplerFinal = null
  #overviewTotal = null
  #overviewCtaPrimary = null
  #trialDurationEl = null
  #trialDurationHeadingEl = null
  #trialDurationOverviewEl = null
  #feedingRecommendationsEl = null
  #frequencyTextEl = null
  #petProfileLinkClickHandler = null
  #boundPageShowHandler = null
  #boundCartDiscountCodesChangeHandler = null
  #boundCartSyncStartHandler = null
  #boundCartSyncHandler = null
  #boundCartSyncErrorHandler = null
  #isSyncInProgress = false
  #hasExternalDiscount = false
  #externalDiscountTitle = ''
  #samplerDiscountBadges = []
  #samplerTotal = 0

  connectedCallback() {
    this.#boxState = BoxState.getInstance()
    this.#subscribeButton = this.querySelector('[data-overview-cta-primary]')?.closest('button')

    this.#samplerCardsContainer = this.querySelector('[data-sampler-cards]')
    this.#subscriptionCardsContainer = this.querySelector('[data-subscription-cards]')
    this.#samplerWeightEl = this.querySelector('[data-sampler-weight]')
    this.#samplerPriceEl = this.querySelector('[data-sampler-price]')
    this.#samplerOriginalPriceEl = this.querySelector('[data-sampler-original-price]')
    this.#subscriptionWeightEl = this.querySelector('[data-subscription-weight]')
    this.#subscriptionPriceEl = this.querySelector('[data-subscription-price]')
    this.#subscriptionPerDayEl = this.querySelector('[data-subscription-per-day]')
    this.#overviewSamplerWeight = this.querySelector('[data-overview-sampler-weight]')
    this.#overviewSamplerOriginal = this.querySelector('[data-overview-sampler-original]')
    this.#overviewSamplerFinal = this.querySelector('[data-overview-sampler-final]')
    this.#overviewTotal = this.querySelector('[data-overview-total]')
    this.#overviewCtaPrimary = this.querySelector('[data-overview-cta-primary]')
    this.#trialDurationEl = this.querySelector('[data-trial-duration]')
    this.#trialDurationHeadingEl = this.querySelector('[data-trial-duration-heading]')
    this.#trialDurationOverviewEl = this.querySelector('[data-trial-duration-overview]')
    this.#feedingRecommendationsEl = this.querySelector('[data-feeding-recommendations]')
    this.#frequencyTextEl = this.querySelector('[data-frequency-text]')

    if (this.#subscribeButton) {
      this.#subscribeButton.addEventListener('click', (e) => this.#handleSubscribeClick(e))
      setDisabled(this.#subscribeButton, !this.#boxState.cartDiscountCodesFetched)

      this.#boundCartDiscountCodesChangeHandler = () => {
        setDisabled(this.#subscribeButton, false)
      }
      this.#boxState.subscribe(EVENT_TYPES.CART_DISCOUNT_CODES_CHANGE, this.#boundCartDiscountCodesChangeHandler)

      this.#boundCartSyncStartHandler = () => this.#handleCartSyncStart()
      this.#boundCartSyncHandler = (e) => this.#updatePriceFromCartState(e.detail)
      this.#boundCartSyncErrorHandler = () => this.#handleCartSyncError()
      window.addEventListener('cart-sync:start', this.#boundCartSyncStartHandler)
      window.addEventListener('cart-sync:complete', this.#boundCartSyncHandler)
      window.addEventListener('cart-sync:error', this.#boundCartSyncErrorHandler)
    }

    const mobileBackBtn = this.querySelector('[data-mobile-back-btn]')
    mobileBackBtn && mobileBackBtn.addEventListener('click', (e) => {
      e.preventDefault()
      this.dispatchEvent(new CustomEvent('summary:back', { bubbles: true }))
    })

    const trialCartEnabled = window.VIVA?.trialCart?.enabled ?? false
    if (trialCartEnabled) {
      this.#petProfileLinkClickHandler = (e) => this.#handlePetProfileLinkClick(e)
      this.addEventListener('click', this.#petProfileLinkClickHandler)
    }

    this.#boundPageShowHandler = (e) => {
      if (e.persisted) {
        this.#resetSubscribeButton()
      }
    }
    window.addEventListener('pageshow', this.#boundPageShowHandler)

    this.#hasExternalDiscount = window.VIVA?.hasExternalDiscounts ?? false
    this.#externalDiscountTitle = window.VIVA?.externalDiscountTitle ?? ''
    this.#samplerDiscountBadges = this.querySelectorAll('[data-sampler-discount-text]')

    this.hidden = false

    updateThawingContainerVisibility(this, this.#boxState.boxConfig?.subscription?.thawingContainerVariantId)
  }

  disconnectedCallback() {
    if (this.#petProfileLinkClickHandler) {
      this.removeEventListener('click', this.#petProfileLinkClickHandler)
      this.#petProfileLinkClickHandler = null
    }
    if (this.#boundPageShowHandler) {
      window.removeEventListener('pageshow', this.#boundPageShowHandler)
      this.#boundPageShowHandler = null
    }
    if (this.#boundCartDiscountCodesChangeHandler) {
      this.#boxState.unsubscribe(EVENT_TYPES.CART_DISCOUNT_CODES_CHANGE, this.#boundCartDiscountCodesChangeHandler)
      this.#boundCartDiscountCodesChangeHandler = null
    }
    if (this.#boundCartSyncStartHandler) {
      window.removeEventListener('cart-sync:start', this.#boundCartSyncStartHandler)
      this.#boundCartSyncStartHandler = null
    }
    if (this.#boundCartSyncHandler) {
      window.removeEventListener('cart-sync:complete', this.#boundCartSyncHandler)
      this.#boundCartSyncHandler = null
    }
    if (this.#boundCartSyncErrorHandler) {
      window.removeEventListener('cart-sync:error', this.#boundCartSyncErrorHandler)
      this.#boundCartSyncErrorHandler = null
    }
  }

  set selections(value) {
    this.#selections = value
    this.#render()
    this.#syncSamplerCart()
  }

  #buildSamplerCartItems() {
    const items = {}
    this.#selections.forEach(({ species, protein, quantity }) => {
      const item = this.#findMatchingItem(species, protein)
      if (item?.variantId) {
        items[item.variantId] = { ...item, quantity }
      }
    })
    return items
  }

  #getSamplerDiscountCode() {
    if (this.#hasExternalDiscount) return null

    const discountInfo = this.#boxState.samplerDiscountInfo
    const defaultCode = this.#boxState.boxConfig?.subscription?.samplerDiscountCode
    const isAffiliate = discountInfo?.code &&
      (!defaultCode || discountInfo.code.toUpperCase() !== defaultCode.toUpperCase())

    return isAffiliate ? discountInfo.code : defaultCode
  }

  #syncSamplerCart() {
    if (this.#selections.length === 0 || Object.keys(this.#boxItems).length === 0) {
      return
    }
    const samplerCartItems = this.#buildSamplerCartItems()
    const discountCode = this.#getSamplerDiscountCode()

    this.#boxState.requestCartSyncWithItems(samplerCartItems, discountCode, { _is_sampler: 'true' })
  }

  set boxItems(value) {
    if (Array.isArray(value)) {
      this.#boxItems = value.reduce((acc, item) => {
        if (item.variantId) {
          acc[item.variantId] = item
        }
        return acc
      }, {})
    } else {
      this.#boxItems = value
    }
    this.#render()
  }

  #findMatchingItem(species, protein) {
    return findItemBySpeciesProtein(this.#boxItems, species, protein)
  }

  #getTotalSamplerQuantity() {
    return this.#selections.reduce((sum, sel) => {
      if (!sel.quantity || sel.quantity < 1) {
        throw new Error(`Invalid quantity for ${sel.species}-${sel.protein}: ${sel.quantity}`)
      }
      return sum + sel.quantity
    }, 0)
  }

  #getGroupInfo(item) {
    const selectionMeta = this.#selectionMetaMap.get(item.variantId)
    const summaryI18n = this.#boxState?.boxConfig?.i18n?.box_builder?.summary || {}

    if (selectionMeta?.species === 'puppy') {
      return {
        label: summaryI18n.group_label_puppy_recipes || '',
        sortOrder: GROUP_SORT_ORDER.PUPPY,
        handle: 'puppy'
      }
    }

    const collection = item.collections?.[0]
    if (!collection) {
      return { label: summaryI18n.group_label_recipes || '', sortOrder: GROUP_SORT_ORDER.DOG, handle: 'default' }
    }

    return {
      label: collection.groupLabel || collection.title,
      sortOrder: collection.sortOrder ?? GROUP_SORT_ORDER.DOG,
      handle: collection.handle
    }
  }

  #groupAndSortItems(items) {
    const groupsMap = new Map()

    items.forEach(item => {
      const { label, sortOrder, handle } = this.#getGroupInfo(item)
      if (!groupsMap.has(handle)) {
        groupsMap.set(handle, { label, items: [], sortOrder })
      }
      groupsMap.get(handle).items.push(item)
    })

    return Array.from(groupsMap.entries())
      .sort((a, b) => a[1].sortOrder - b[1].sortOrder)
  }

  #calculateGroupSubtotal(items) {
    return items.reduce((sum, item) => sum + (item.quantity * PRODUCT_PACKAGE_WEIGHT_LBS), 0)
  }

  #renderGroupedRecipeCards(itemsWithQuantity) {
    const sortedGroups = this.#groupAndSortItems(itemsWithQuantity)

    if (sortedGroups.length === 0) return ''

    if (sortedGroups.length === 1) {
      const [, { items }] = sortedGroups[0]
      const cards = items.map(item => this.#renderRecipeCard(item, item.quantity)).join('')
      return `<div class="v:flex v:flex-col v:gap-4">${cards}</div>`
    }

    const summaryI18n = this.#boxState?.boxConfig?.i18n?.box_builder?.summary || {}
    return sortedGroups.map(([groupHandle, { label, items }]) => {
      const subtotal = this.#calculateGroupSubtotal(items)
      const cards = items.map(item => this.#renderRecipeCard(item, item.quantity)).join('')

      return `
        <div class="v:flex v:flex-col v:gap-4">
          <div class="v:flex v:items-center v:justify-between">
            <p class="v:label-md v:font-bold v:text-brand-brown">${label}</p>
            <p class="v:label-md v:font-bold v:text-brand-brown">
              ${summaryI18n.subtotal_label || ''} <span class="v:text-brand-orange-dark">${subtotal} lbs</span>
            </p>
          </div>
          <div class="v:flex v:flex-col v:gap-4">
            ${cards}
          </div>
        </div>
      `
    }).join('')
  }

  #renderRecipeCard(item, quantity) {
    if (!item) return ''

    return `
      <recipe-card
        data-layout="horizontal"
        data-readonly="true"
        data-quantity="${quantity}"
        data-product-title="${item.productTitle || ''}"
        data-variant-option1="${item.variantOption1 || ''}"
        data-price="${item.price || 0}"
        data-featured-image-url="${item.featuredImageUrl || ''}"
        data-featured-image-width="${item.featuredImageWidth || 0}"
        data-product-url="${item.productUrl || ''}"
        data-variant-id="${item.variantId || ''}">
      </recipe-card>
    `
  }

  #calculateTotal(quantity) {
    return this.#selections.reduce((total, { species, protein }) => {
      const item = this.#findMatchingItem(species, protein)
      if (item) {
        return total + (item.price * quantity)
      }
      return total
    }, 0)
  }

  #formatPrice(cents) {
    return `$${(cents / 100).toFixed(2)}`
  }

  #calculateTrialDuration() {
    const petProfiles = this.#boxState.petProfiles

    if (!petProfiles || petProfiles.length === 0) {
      return MINIMUM_TRIAL_DURATION_WEEKS
    }

    const transformedPets = transformPetsForRecommendation(petProfiles)
    const totalDailyOunces = transformedPets.reduce((sum, pet) => {
      return sum + getFeedingAmount(pet)
    }, 0)

    if (totalDailyOunces <= 0) {
      return MINIMUM_TRIAL_DURATION_WEEKS
    }

    const totalDailyLbs = convertOunceToPound(totalDailyOunces)
    const totalWeeklyLbs = totalDailyLbs * 7
    const calculatedWeeks = Math.floor(MIN_SAMPLER_TRIAL_WEIGHT_LBS / totalWeeklyLbs)

    return Math.max(MINIMUM_TRIAL_DURATION_WEEKS, calculatedWeeks)
  }

  #calculateSubscriptionQuantities() {
    return calculateSamplerSubscriptionQuantities(
      this.#boxState.petProfiles,
      this.#selections,
      this.#boxItems
    )
  }

  async #handleSubscribeClick(e) {
    e.preventDefault()

    if (!this.#subscribeButton || this.#subscribeButton.disabled) return

    setDisabled(this.#subscribeButton, true)

    try {
      CustomerService.getInstance().clearEligibilityCache()
      await this.#prepareSubscriptionCheckout()
    } catch (error) {
      console.error('Subscribe failed:', error)
      this.#resetSubscribeButton()
    }
  }

  #resetSubscribeButton() {
    setDisabled(this.#subscribeButton, false)
    this.#render()
  }

  #updatePriceFromCartState(cartState) {
    this.#isSyncInProgress = false

    if (!this.#hasExternalDiscount) {
      setDisabled(this.#subscribeButton, false)
      return
    }

    const { totalPrice } = cartState

    if (this.#overviewCtaPrimary) {
      setTextContent(this.#overviewCtaPrimary, `Subscribe for ${this.#formatPrice(totalPrice)}`)
    }
    if (this.#overviewTotal) {
      setTextContent(this.#overviewTotal, this.#formatPrice(totalPrice))
    }

    const hasActualDiscount = totalPrice < this.#samplerTotal

    if (hasActualDiscount) {
      const discountPercentage = Math.round((1 - totalPrice / this.#samplerTotal) * 100)
      const discountText = `${discountPercentage}% OFF`

      this.#samplerDiscountBadges.forEach(badge => {
        setTextContent(badge, discountText)
      })

      setTextContent(this.#samplerPriceEl, this.#formatPrice(totalPrice))
      setTextContent(this.#overviewSamplerFinal, this.#formatPrice(totalPrice))

      if (this.#samplerOriginalPriceEl) this.#samplerOriginalPriceEl.hidden = false
      if (this.#overviewSamplerOriginal) this.#overviewSamplerOriginal.hidden = false

      const discountBadges = this.querySelectorAll('[data-sampler-discount-badge]')
      discountBadges.forEach(badge => { badge.hidden = false })
    } else {
      setTextContent(this.#samplerPriceEl, this.#formatPrice(totalPrice))
      setTextContent(this.#overviewSamplerFinal, this.#formatPrice(totalPrice))

      if (this.#samplerOriginalPriceEl) this.#samplerOriginalPriceEl.hidden = true
      if (this.#overviewSamplerOriginal) this.#overviewSamplerOriginal.hidden = true

      const discountBadges = this.querySelectorAll('[data-sampler-discount-badge]')
      discountBadges.forEach(badge => { badge.hidden = true })
    }

    setDisabled(this.#subscribeButton, false)
  }

  #handleCartSyncStart() {
    if (!this.#hasExternalDiscount) return

    this.#isSyncInProgress = true
    this.#showPriceSkeletons()
    setDisabled(this.#subscribeButton, true)
  }

  #handleCartSyncError() {
    this.#isSyncInProgress = false
    this.#hidePriceSkeletons()
    setDisabled(this.#subscribeButton, false)
  }

  #showPriceSkeletons() {
    const skeletonHtml = '<span class="box-summary-view__price-skeleton"></span>'

    if (this.#samplerPriceEl) {
      this.#samplerPriceEl.innerHTML = skeletonHtml
    }
    if (this.#overviewSamplerFinal) {
      this.#overviewSamplerFinal.innerHTML = skeletonHtml
    }
    if (this.#overviewTotal) {
      this.#overviewTotal.innerHTML = skeletonHtml
    }
    if (this.#overviewCtaPrimary) {
      const summaryI18n = this.#boxState?.boxConfig?.i18n?.box_builder?.summary || {}
      setTextContent(this.#overviewCtaPrimary, summaryI18n.calculating || '')
    }
  }

  #hidePriceSkeletons() {
    if (!this.#hasExternalDiscount || !this.#isSyncInProgress) return

    const samplerFinal = this.#samplerTotal * 0.5
    setTextContent(this.#samplerPriceEl, this.#formatPrice(samplerFinal))
    setTextContent(this.#overviewSamplerFinal, this.#formatPrice(samplerFinal))
    setTextContent(this.#overviewTotal, this.#formatPrice(samplerFinal))
    setTextContent(this.#overviewCtaPrimary, `Subscribe for ${this.#formatPrice(samplerFinal)}`)
  }

  #handlePetProfileLinkClick(e) {
    const link = e.target.closest('a[href]')
    if (!link) return

    const mealPlanUrl = this.dataset.mealPlanUrl || DEFAULT_MEAL_PLAN_URL
    if (!link.href.includes(mealPlanUrl)) return
  }

  async #prepareSubscriptionCheckout() {
    await this.#boxState.waitForCartSync()

    if (!this.#subscriptionData) {
      console.error('BoxSummaryView: subscriptionData not calculated - render must be called first')
      this.#resetSubscribeButton()
      return
    }

    const boxConfig = this.#boxState.boxConfig

    if (!boxConfig?.bundle?.productId || !boxConfig?.bundle?.variantId) {
      console.error('BoxSummaryView: Bundle configuration missing from window.VIVA.bundle')
      this.#resetSubscribeButton()
      return
    }

    const { quantities: subscriptionQuantities, frequency } = this.#subscriptionData
    const trialDuration = this.#trialDuration

    const quantityMap = new Map()
    subscriptionQuantities.forEach(({ species, protein, quantity }) => {
      quantityMap.set(`${species}-${protein}`, quantity)
    })

    const itemsForSelections = this.#selections.map(({ species, protein, quantity }) => {
      const item = this.#findMatchingItem(species, protein)
      if (!item) return null

      return {
        ...item,
        quantity,
        futureQuantity: quantityMap.get(`${species}-${protein}`) || 2
      }
    }).filter(Boolean)

    const sellingPlanMap = Object.fromEntries(
      itemsForSelections.map(item => [item.productId, getSellingPlanForItem(item.productId, frequency, window.all_products)])
    )

    const missingSellingPlans = itemsForSelections.filter(item => !sellingPlanMap[item.productId])
    if (missingSellingPlans.length > 0) {
      throw new Error(`Missing selling plan for ${missingSellingPlans[0].productTitle}`)
    }

    const bundleSelections = buildBundleSelections({
      items: itemsForSelections,
      sellingPlanMap,
      includeFutureQuantity: true
    })

    const bundle = {
      externalProductId: boxConfig.bundle.productId,
      externalVariantId: boxConfig.bundle.variantId,
      selections: bundleSelections.map(({ futureQuantity, ...selection }) => selection)
    }

    const bundleItems = await getDynamicBundleItems(bundle, boxConfig.bundle.handle || '')

    const gwpItem = shouldAddGwpToCartFromSelections(this.#selections, ({ species, protein }) =>
      this.#findMatchingItem(species, protein)
    ) ? buildGwpCartItem() : null

    const cartItems = await buildCartItems({
      bundleItems,
      bundleSelections,
      thawingContainerVariantId: boxConfig.subscription.thawingContainerVariantId,
      gwpItem,
      samplerOptions: {
        enabled: true,
        frequency,
        trialDuration
      }
    })

    const { rootPath } = boxConfig

    await fetch(rootPath + 'cart/clear.js', {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
        'X-Requested-With': 'XMLHttpRequest'
      }
    })

    await fetch(rootPath + 'cart/add.js', {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
        'X-Requested-With': 'XMLHttpRequest'
      },
      body: JSON.stringify({ items: cartItems })
    })

    const cartAttributes = {}
    const currentEmail = this.#boxState.getCurrentEmail()
    if (currentEmail) {
      cartAttributes._petOwnerEmail = currentEmail
    }
    const petProfiles = this.#boxState.petProfiles
    if (petProfiles?.length > 0) {
      cartAttributes._petProfileIds = petProfiles.map(p => p.id).filter(Boolean).join(',')
    }

    if (Object.keys(cartAttributes).length > 0) {
      await fetch(rootPath + 'cart/update.js', {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'X-Requested-With': 'XMLHttpRequest'
        },
        body: JSON.stringify({ attributes: cartAttributes })
      })
    }

    this.#boxState.markCheckoutStarted()

    window.location.href = boxConfig.rootPath + 'checkout'
  }

  #render() {
    if (this.#selections.length === 0 || Object.keys(this.#boxItems).length === 0) {
      return
    }

    this.#selectionMetaMap.clear()
    this.#selections.forEach(({ species, protein }) => {
      const item = this.#findMatchingItem(species, protein)
      if (item?.variantId) {
        this.#selectionMetaMap.set(item.variantId, { species, protein })
      }
    })

    const trialCartEnabled = window.VIVA?.trialCart?.enabled ?? false
    if (trialCartEnabled && this.#trialDuration === null) {
      this.#trialDuration = this.#calculateTrialDuration()
    }

    const samplerItemsWithQuantity = this.#selections.map(({ species, protein, quantity }) => {
      const item = this.#findMatchingItem(species, protein)
      if (item) {
        return { ...item, quantity }
      }
      return null
    }).filter(Boolean)

    const samplerCards = this.#renderGroupedRecipeCards(samplerItemsWithQuantity)

    this.#subscriptionData = this.#calculateSubscriptionQuantities()
    const { quantities: subscriptionQuantities, frequency } = this.#subscriptionData

    const subscriptionItemsWithQuantity = subscriptionQuantities.map(({ species, protein, quantity }) => {
      const item = this.#findMatchingItem(species, protein)
      if (item) {
        return { ...item, quantity }
      }
      return null
    }).filter(Boolean)

    const subscriptionCards = this.#renderGroupedRecipeCards(subscriptionItemsWithQuantity)

    const samplerTotal = this.#selections.reduce((total, { species, protein, quantity }) => {
      const item = this.#findMatchingItem(species, protein)
      if (item) {
        return total + (item.price * quantity)
      }
      return total
    }, 0)
    this.#samplerTotal = samplerTotal
    const samplerWeight = this.#getTotalSamplerQuantity() * PRODUCT_PACKAGE_WEIGHT_LBS
    const samplerFinal = this.#hasExternalDiscount
      ? samplerTotal
      : samplerTotal * 0.5

    const subscriptionTotal = subscriptionQuantities.reduce((total, { species, protein, quantity }) => {
      const item = this.#findMatchingItem(species, protein)
      if (item) {
        return total + (item.price * quantity)
      }
      return total
    }, 0)
    const subscriptionWeight = subscriptionQuantities.reduce((total, { quantity }) => {
      return total + (quantity * PRODUCT_PACKAGE_WEIGHT_LBS)
    }, 0)

    const subscriptionPerDay = subscriptionTotal / (frequency * 7)

    setInnerHTML(this.#samplerCardsContainer, samplerCards)
    setInnerHTML(this.#subscriptionCardsContainer, subscriptionCards)

    setTextContent(this.#samplerWeightEl, `${samplerWeight} lbs`)
    if (!(this.#hasExternalDiscount && this.#isSyncInProgress)) {
      setTextContent(this.#samplerPriceEl, this.#formatPrice(samplerFinal))
    }
    setTextContent(this.#samplerOriginalPriceEl, this.#formatPrice(samplerTotal))
    setTextContent(this.#subscriptionWeightEl, `${subscriptionWeight} lbs`)
    setTextContent(this.#subscriptionPriceEl, this.#formatPrice(subscriptionTotal))
    setTextContent(this.#subscriptionPerDayEl, this.#formatPrice(subscriptionPerDay) + '/day')

    setTextContent(this.#overviewSamplerWeight, `${samplerWeight} lb`)
    setTextContent(this.#overviewSamplerOriginal, this.#formatPrice(samplerTotal))

    if (this.#hasExternalDiscount && this.#isSyncInProgress) {
      this.#showPriceSkeletons()
    } else {
      setTextContent(this.#overviewSamplerFinal, this.#formatPrice(samplerFinal))
      setTextContent(this.#overviewTotal, this.#formatPrice(samplerFinal))
      setTextContent(this.#overviewCtaPrimary, `Subscribe for ${this.#formatPrice(samplerFinal)}`)
    }

    if (this.#hasExternalDiscount) {
      if (this.#samplerOriginalPriceEl) {
        this.#samplerOriginalPriceEl.hidden = true
      }
      if (this.#overviewSamplerOriginal) {
        this.#overviewSamplerOriginal.hidden = true
      }
      const discountBadges = this.querySelectorAll('[data-sampler-discount-badge]')
      discountBadges.forEach(badge => {
        badge.hidden = true
      })
    }

    if (this.#trialDuration !== null) {
      const trialText = `${this.#trialDuration} ${this.#trialDuration === 1 ? 'week' : 'weeks'}`
      setTextContent(this.#trialDurationEl, trialText)
      setTextContent(this.#trialDurationHeadingEl, `In ${trialText}`)
      setTextContent(this.#trialDurationOverviewEl, trialText)
    }

    setTextContent(this.#frequencyTextEl, `every ${frequency} weeks`)

    const discountText = this.#hasExternalDiscount
      ? this.#externalDiscountTitle
      : '50% OFF'
    this.#samplerDiscountBadges.forEach(badge => {
      setTextContent(badge, discountText)
    })

    const loadedPetProfiles = this.#loadPetProfiles()
    this.#renderFeedingRecommendations(loadedPetProfiles, frequency)

    this.#fireSamplerAnalyticsEvent({
      samplerItemsWithQuantity,
      subscriptionItemsWithQuantity,
      samplerTotal,
      subscriptionTotal,
      frequency,
      loadedPetProfiles
    })
  }

  #loadPetProfiles() {
    const pets = this.#boxState.petProfiles
    if (!Array.isArray(pets) || pets.length === 0) return []

    const transformedPets = transformPetsForRecommendation(pets)
    return pets.map((pet, index) => {
      const ageInMonths = parseInt(pet.ageYears || 0) * 12 + parseInt(pet.ageMonths || 0)
      return {
        name: pet.name,
        species: pet.species,
        gender: pet.sex,
        age_months: ageInMonths || 1,
        activity_level: pet.activityLevel,
        weight: ageInMonths < 12 ? pet.currentWeight : (pet.weight || pet.idealWeight),
        dailyOz: getFeedingAmount(transformedPets[index])
      }
    })
  }

  #renderFeedingRecommendations(petProfiles, frequency) {
    if (!this.#feedingRecommendationsEl) return

    if (petProfiles.length === 0) {
      setInnerHTML(this.#feedingRecommendationsEl, '')
      return
    }

    const mealPlanUrl = this.dataset.mealPlanUrl || DEFAULT_MEAL_PLAN_URL

    const petListHtml = petProfiles.map(pet =>
      renderPetRecommendationHtml(pet, frequency, mealPlanUrl)
    ).join('')

    setInnerHTML(this.#feedingRecommendationsEl, petListHtml)
  }

  #fireSamplerAnalyticsEvent({ samplerItemsWithQuantity, subscriptionItemsWithQuantity, samplerTotal, subscriptionTotal, frequency, loadedPetProfiles }) {
    if (!this.#boxState.pendingSamplerAnalytics) return
    this.#boxState.setPendingSamplerAnalytics(false)

    const formatItem = (item) => ({
      sku: item.variantSku || '',
      product_title: item.productTitle,
      variant_option: item.variantOption1,
      price: item.price / 100,
      quantity: item.quantity,
      subtotal: (item.price * item.quantity) / 100
    })

    this.#boxState.triggerSamplerCompleted({
      feeding_recs: loadedPetProfiles,
      sampler_items: samplerItemsWithQuantity.map(formatItem),
      sampler_subtotal: samplerTotal / 100,
      subscription_items: subscriptionItemsWithQuantity.map(formatItem),
      subscription_subtotal: subscriptionTotal / 100,
      frequency,
      trial_duration: this.#trialDuration
    })
  }
}

if (!customElements.get('box-summary-view')) {
  customElements.define('box-summary-view', BoxSummaryView)
}